import React, { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api, { reportAPI } from '../services/api';

const card = { background:'white', borderRadius:'12px', border:'1px solid #e2e8f0', padding:'16px 20px', marginBottom:'12px', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' };
const inp = { border:'1px solid #e2e8f0', borderRadius:'8px', padding:'8px 12px', fontSize:'13px', outline:'none' };

export default function CustomerAnalytics() {
  const { user } = useAuth();
  const [period, setPeriod] = useState('monthly');
  const isSubDistAdmin = user?.role === 'subdist_admin';
  const [lcoCountsStatus, setLcoCountsStatus] = React.useState(null);
  const [lcoCounts, setLcoCounts] = React.useState([]);
  const [lcoCountsLoading, setLcoCountsLoading] = React.useState(false);
  const [showLcoCounts, setShowLcoCounts] = React.useState(false);
  const mySubCode = user?.sub_dist_code || JSON.parse(localStorage.getItem('user')||'{}').sub_dist_code || '';
  const [filterSub, setFilterSub] = useState(user?.role==='subdist_admin'?(user?.sub_dist_code||''):'');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [expandedSubs, setExpandedSubs] = useState({});
  const [showTrend, setShowTrend] = useState(false);
  const [monthlyData, setMonthlyData] = useState([]);
  const [showMonthly, setShowMonthly] = useState(false);
  const [expandedMonth, setExpandedMonth] = useState(null);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [monthlyYear, setMonthlyYear] = useState(new Date().getFullYear().toString());
  const subDists = ['KB13A','KB13B','KB13C','KB13D','KB13E','KB13F','KB13H','KB13J','KB13N'];

  const isAdmin = user && ['director','dist_admin','dist_head','tenant_admin','superadmin','subdist_admin'].includes(user.role);
  const [bmsUsers, setBmsUsers] = React.useState(null);
  const [bmsUsersLoading, setBmsUsersLoading] = React.useState(false);
  const [bmsUsersSub, setBmsUsersSub] = React.useState(null);
  const [statusChanges, setStatusChanges] = React.useState(null);
  const [statusChangesLoading, setStatusChangesLoading] = React.useState(false);
  const [showPending, setShowPending] = React.useState(false);
  const pendingFetched = React.useRef(false);
  const [pendingByLco, setPendingByLco] = React.useState([]);
  const [expandedLcos, setExpandedLcos] = React.useState({});
  const [pendingLcoLoading, setPendingLcoLoading] = React.useState(false);
  const [pendingSubs, setPendingSubs] = React.useState([]);
  const [showStatusChanges, setShowStatusChanges] = React.useState(false);
  const loadMonthlyTracker = async () => {
    if(monthlyData.length>0){setShowMonthly(p=>!p);return;}
    setMonthlyLoading(true);
    setShowMonthly(true);
    try {
      const r = await api.get('/reports/monthly-tracker');
      setMonthlyData(r.data.data||[]);
    } catch(e){console.error(e);}
    setMonthlyLoading(false);
  };

  const fetchLcoCounts = async (status) => {
    if(!filterSub) return;
    if(lcoCountsStatus === status && showLcoCounts) { setShowLcoCounts(false); return; }
    setLcoCountsStatus(status);
    setShowLcoCounts(true);
    setLcoCountsLoading(true);
    setLcoCounts([]);
    try {
      const r = await api.get('/reports/counts-by-lco', {params:{sub_dist:filterSub, status:status==='total'?undefined:status}});
      if(r.data.success) setLcoCounts(r.data.data||[]);
    } catch(e){}
    setLcoCountsLoading(false);
  };

  const fetchPending = async () => {
    if(pendingSubs.length===0 && data?.sub_districts) {
      setPendingSubs((data.sub_districts||[]).filter(s=>s.pending>0).sort((a,b)=>b.pending-a.pending));
    }
    if(isSubDistAdmin) {
      if(showPending){ setShowPending(false); return; }
      setShowPending(true);
      if(pendingFetched.current){ return; }
      setPendingLcoLoading(true);
      try {
        const subCode = mySubCode || filterSub || 'KB13B';
        const resp = await api.get('/reports/pending-by-lco', {params:{sub_dist:subCode,_t:Date.now()}});
        const lcoData = resp.data.data||[];
        setPendingByLco(lcoData);
        pendingFetched.current = true;
      } catch(err){ console.error('LCO pending error:', err); }
      setPendingLcoLoading(false);
    } else {
      setShowPending(s=>!s);
    }
  };

  const fetchStatusChanges = async () => {
    if(showStatusChanges) { setShowStatusChanges(false); return; }
    setStatusChangesLoading(true);
    try {
      const r = await reportAPI.bmsStatusChanges(filterSub||null);
      setStatusChanges(r.data);
      setShowStatusChanges(true);
    } catch(e) { console.error(e); }
    setStatusChangesLoading(false);
  };

  const fetchBmsUsers = async (subCode) => {
    const actualCode = subCode || 'KB13';
    if(bmsUsersSub===actualCode) { setBmsUsers(null); setBmsUsersSub(null); return; }
    setBmsUsersLoading(true); setBmsUsersSub(actualCode);
    try { const r = await reportAPI.bmsUsers(actualCode); setBmsUsers(r.data.users); }
    catch(e) { setBmsUsers([]); }
    finally { setBmsUsersLoading(false); }
  };

  const loadReport = useCallback(async () => {
    // For subdist_admin ensure we have sub_dist
    const effectiveSub = filterSub || mySubCode || '';
    if(isSubDistAdmin && !effectiveSub) {
      console.warn('subdist_admin has no sub_dist_code');
      return;
    }
    setLoading(true); setData(null);
    try {
      const params = { period };
      if(effectiveSub) params.sub_dist = effectiveSub;
      const r = await api.get('/reports/customer-analytics', { params });
      setData(r.data);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [period, filterSub]);

  // Auto-load for subdist_admin
  React.useEffect(() => {
    if(isSubDistAdmin) loadReport();
  // eslint-disable-next-line
  }, []);

  const printReport = () => {
    const w = window.open('','_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>Customer Analytics Report</title>
    <style>
      body{font-family:Arial,sans-serif;padding:0;margin:0;color:#1e293b;font-size:13px}
      .header{background:linear-gradient(135deg,#1e3a5f,#2563eb);color:white;padding:24px 32px}
      .header h1{margin:0 0 4px;font-size:20px} .header p{margin:0;opacity:.7;font-size:12px}
      .content{padding:20px 32px}
      .grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:20px}
      .stat{border-radius:8px;padding:14px;text-align:center}
      .stat .val{font-size:24px;font-weight:800} .stat .lbl{font-size:10px;color:#64748b;margin-top:2px}
      table{width:100%;border-collapse:collapse;margin-bottom:16px;font-size:12px}
      th{background:#f1f5f9;padding:8px;text-align:left;font-weight:700}
      td{padding:7px 8px;border-bottom:1px solid #f1f5f9}
      h2{font-size:14px;font-weight:800;margin:20px 0 8px;border-bottom:2px solid #2563eb;padding-bottom:4px}
      .footer{margin-top:24px;padding:12px;border-top:1px solid #e2e8f0;font-size:10px;color:#94a3b8;text-align:center}
      @media print{body{print-color-adjust:exact}}
    </style></head><body>
    <div class="header">
      <h1>📊 Customer Analytics Report</h1>
      <p>${period.charAt(0).toUpperCase()+period.slice(1)} Report • PDIC Network • Generated ${new Date().toLocaleString('en-IN')} • Last Sync: ${data?.last_sync?new Date(data.last_sync).toLocaleString('en-IN'):'-'}</p>
    </div>
    <div class="content">
      <h2>OVERALL SUMMARY</h2>
      <div class="grid">
        <div class="stat" style="background:#eff6ff"><div class="val" style="color:#2563eb">${data?.summary.total?.toLocaleString()}</div><div class="lbl">Total Customers</div></div>
        <div class="stat" style="background:#f0fdf4"><div class="val" style="color:#15803d">${data?.summary.active?.toLocaleString()}</div><div class="lbl">Active (${data?.summary.active_rate}%)</div></div>
        <div class="stat" style="background:#fef2f2"><div class="val" style="color:#dc2626">${data?.summary.suspended?.toLocaleString()}</div><div class="lbl">Suspended</div></div>
        <div class="stat" style="background:#fffbeb"><div class="val" style="color:#d97706">${data?.summary.inactive?.toLocaleString()}</div><div class="lbl">Inactive</div></div>
        <div class="stat" style="background:#f5f3ff"><div class="val" style="color:#7c3aed">${data?.summary.pending?.toLocaleString()}</div><div class="lbl">Pending Activation</div></div>
        <div class="stat" style="background:#f8fafc"><div class="val" style="color:#64748b">${data?.summary.dormant?.toLocaleString()}</div><div class="lbl">Dormant</div></div>
        <div class="stat" style="background:#f0fdf4"><div class="val" style="color:#15803d">+${data?.summary.new_active?.toLocaleString()}</div><div class="lbl">New Active Today</div></div>
        <div class="stat" style="background:#fef2f2"><div class="val" style="color:#dc2626">-${data?.summary.churned?.toLocaleString()}</div><div class="lbl">Churned Today</div></div>
      </div>
      <h2>SUB-DISTRICT BREAKDOWN</h2>
      <table><thead><tr><th>Sub-District</th><th>Total</th><th>Active</th><th>Rate</th><th>Suspended</th><th>Inactive</th><th>Pending</th><th>Dormant</th></tr></thead>
      <tbody>${(data?.sub_districts||[]).map(s=>`<tr><td>${s._id||'KB13'}</td><td>${s.total}</td><td style="color:#15803d;font-weight:700">${s.active}</td><td>${Math.round(s.active/s.total*100)}%</td><td style="color:#dc2626">${s.suspended}</td><td>${s.inactive}</td><td style="color:#7c3aed">${s.pending}</td><td>${s.dormant}</td></tr>`).join('')}</tbody></table>
      <h2>TOP ACTIVE PLANS</h2>
      <table><thead><tr><th>Plan</th><th>Subscribers</th></tr></thead>
      <tbody>${(data?.top_plans||[]).map(p=>`<tr><td>${p._id}</td><td>${p.count}</td></tr>`).join('')}</tbody></table>
      <div class="footer">PDIC Network Operations • Customer Analytics Report • Data from BMS</div>
    </div></body></html>`);
    w.document.close();
    setTimeout(()=>w.print(), 500);
  };

  if(!isAdmin) return <div style={{padding:'40px',textAlign:'center',color:'#94a3b8'}}>Access restricted.</div>;

  return (
    <div style={{padding:'20px',maxWidth:'1100px',margin:'0 auto'}}>
      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'20px',flexWrap:'wrap',gap:'10px'}}>
        <div>
          <div style={{fontSize:'20px',fontWeight:'800',color:'#1e293b'}}>📊 Customer Analytics</div>
          <div style={{fontSize:'12px',color:'#64748b',marginTop:'2px'}}>
            Active / Suspended / Inactive trends from BMS data
            {data?.last_sync && <span style={{marginLeft:'8px',color:'#94a3b8'}}>• Last sync: {new Date(data.last_sync).toLocaleString('en-IN',{dateStyle:'medium',timeStyle:'short'})}</span>}
          </div>
        </div>
        {data && <div style={{display:'flex',gap:'8px'}}>
          <button onClick={()=>{
            // Excel/CSV export of summary
            const rows = [['Category','Count']];
            rows.push(['Total Customers', data.summary.total||0]);
            rows.push(['Active', data.summary.active||0]);
            rows.push(['Suspended', data.summary.suspended||0]);
            rows.push(['Inactive', data.summary.inactive||0]);
            rows.push(['Dormant', data.summary.dormant||0]);
            rows.push(['Pending Activation', data.summary.pending||0]);
            rows.push(['New Active Today', data.summary.new_active||0]);
            rows.push(['']);
            rows.push(['Sub-District','Total','Active','Suspended','Inactive','Dormant','Pending']);
            (data.sub_districts||[]).forEach(s=>rows.push([s._id,s.total,s.active,s.suspended,s.inactive,s.dormant,s.pending]));
            const csv = rows.map(r=>r.join(',')).join('\n');
            const blob = new Blob([csv],{type:'text/csv'});
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = 'customer_analytics_'+(filterSub||'all')+'_'+new Date().toISOString().split('T')[0]+'.csv';
            a.click();
          }} style={{background:'#16a34a',color:'white',border:'none',borderRadius:'8px',padding:'10px 18px',cursor:'pointer',fontSize:'13px',fontWeight:'600'}}>📥 Excel</button>
          <button onClick={printReport} style={{background:'#2563eb',color:'white',border:'none',borderRadius:'8px',padding:'10px 18px',cursor:'pointer',fontSize:'13px',fontWeight:'600'}}>🖨️ Print / PDF</button>
        </div>}
      </div>

      {/* Filters */}
      <div style={{...card,padding:'14px 16px',display:'flex',gap:'10px',flexWrap:'wrap',alignItems:'center',marginBottom:'16px'}}>
        <select style={{...inp,width:'130px'}} value={period} onChange={e=>setPeriod(e.target.value)}>
          <option value="daily">Today</option>
          <option value="weekly">Last 7 Days</option>
          <option value="monthly">Last 30 Days</option>
        </select>
        {!isSubDistAdmin && <select style={{...inp,width:'130px'}} value={filterSub} onChange={e=>setFilterSub(e.target.value)}>
          <option value="">All Sub-Districts</option>
          {subDists.map(s=><option key={s} value={s}>{s}</option>)}
        </select>}
        <button id="ca-generate-btn" onClick={loadReport} disabled={loading} style={{background:'#2563eb',color:'white',border:'none',borderRadius:'8px',padding:'9px 20px',cursor:'pointer',fontSize:'13px',fontWeight:'600'}}>
          {loading?'Loading...':'🔍 Generate'}
        </button>
      </div>

      {!data && !loading && (
        <div style={{textAlign:'center',padding:'60px',color:'#94a3b8'}}>
          <div style={{fontSize:'48px',marginBottom:'12px'}}>📊</div>
          <div style={{fontWeight:'600',fontSize:'15px'}}>Click Generate to load customer analytics</div>
          <div style={{fontSize:'12px',marginTop:'4px'}}>Based on daily BMS data uploads</div>
        </div>
      )}

      {data && (
        <div>
          {/* Summary Cards */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:'10px',marginBottom:'16px'}}>
            {[
              {l:'Total Customers',v:data.summary.total?.toLocaleString(),c:'#2563eb',bg:'#eff6ff',icon:'👥',lco:'total'},
              {l:`Active (${data.summary.active_rate}%)`,v:data.summary.active?.toLocaleString(),c:'#15803d',bg:'#f0fdf4',lco:'Active',icon:'✅'},
              {l:'Suspended',v:data.summary.suspended?.toLocaleString(),c:'#dc2626',bg:'#fef2f2',icon:'🚫',lco:'Suspended'},
              {l:'Inactive',v:data.summary.inactive?.toLocaleString(),c:'#d97706',bg:'#fffbeb',icon:'💤',lco:'Inactive'},
              {l:'Pending Activation',v:data.summary.pending?.toLocaleString(),c:'#7c3aed',bg:'#f5f3ff',icon:'⏳',onClick:fetchPending,lco:'New - Activation Pending'},

              {l:'New Active Today',v:'+'+data.summary.new_active?.toLocaleString(),c:'#15803d',bg:'#dcfce7',icon:'🆕',onClick:fetchStatusChanges},
              {l: data.summary.active_diff>=0 ? 'Growth Today' : 'Churned Today',
               v: data.summary.active_diff>=0 ? '+'+data.summary.active_diff?.toLocaleString() : '-'+data.summary.churned?.toLocaleString(),
               c: data.summary.active_diff>=0 ? '#10b981' : '#dc2626',
               bg: data.summary.active_diff>=0 ? '#ecfdf5' : '#fee2e2',
               icon: data.summary.active_diff>=0 ? '📈' : '📉'},
            ].map(s=>(
              <div key={s.l} onClick={()=>{
                if(s.onClick) s.onClick();
                if(s.lco&&filterSub&&!isSubDistAdmin) fetchLcoCounts(s.lco);
              }} style={{background:s.bg,borderRadius:'10px',padding:'14px',cursor:(s.lco&&filterSub&&!isSubDistAdmin)||s.onClick?'pointer':'default',transition:'all 0.15s',boxShadow:(s.lco&&filterSub&&!isSubDistAdmin)||s.onClick?'0 1px 4px rgba(0,0,0,0.08)':'none',border:(s.lco&&filterSub&&!isSubDistAdmin)&&showLcoCounts&&lcoCountsStatus===s.lco?'2px solid #0369a1':'2px solid transparent',textAlign:'center',cursor:s.onClick?'pointer':'default',boxShadow:s.onClick?'0 0 0 2px #16a34a33':undefined}}>
                <div style={{fontSize:'18px',marginBottom:'4px'}}>{s.icon}</div>
                <div style={{fontSize:'22px',fontWeight:'800',color:s.c}}>{s.v}</div>
                <div style={{fontSize:'10px',color:'#64748b',marginTop:'2px'}}>{s.l}</div>
              </div>
            ))}
          </div>

          {/* LCO Counts Breakdown - for dist_admin/director when sub selected */}
          {showLcoCounts && filterSub && !isSubDistAdmin && (
            <div style={{background:'#f0f9ff',borderRadius:'12px',padding:'16px',marginBottom:'16px',border:'1px solid #bae6fd'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'12px'}}>
                <div style={{fontWeight:'700',fontSize:'13px',color:'#0369a1'}}>
                  📊 LCO Breakdown — {filterSub} — {lcoCountsStatus==='total'?'All Customers':lcoCountsStatus}
                </div>
                <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
                  <span style={{fontSize:'12px',color:'#0369a1',fontWeight:'700'}}>
                    Total: {lcoCounts.reduce((a,l)=>a+l.count,0).toLocaleString()}
                  </span>
                  <button onClick={()=>{
                    // Excel export
                    const rows = [['#','LCO Code','Count','Share%']];
                    const tot = lcoCounts.reduce((a,l)=>a+l.count,0);
                    lcoCounts.forEach((l,i)=>rows.push([i+1,l._id||'Unknown',l.count,(l.count/tot*100).toFixed(1)+'%']));
                    const csv = rows.map(r=>r.join(',')).join('\n');
                    const blob = new Blob([csv],{type:'text/csv'});
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(blob);
                    a.download = filterSub+'_'+lcoCountsStatus+'_lco_counts.csv';
                    a.click();
                  }} style={{padding:'4px 10px',background:'#16a34a',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'11px',fontWeight:'600'}}>
                    📥 Excel
                  </button>
                  <button onClick={()=>{
                    // PDF export via print
                    const tot = lcoCounts.reduce((a,l)=>a+l.count,0);
                    const rows = lcoCounts.map((l,i)=>`<tr style="background:${i%2?'#f0f9ff':'white'}"><td style="padding:6px 10px">${i+1}</td><td style="padding:6px 10px;font-weight:700;color:#0369a1">${l._id||'Unknown'}</td><td style="padding:6px 10px;text-align:right;font-weight:700">${l.count.toLocaleString()}</td><td style="padding:6px 10px;text-align:right;color:#64748b">${(l.count/tot*100).toFixed(1)}%</td></tr>`).join('');
                    const w = window.open('','_blank');
                    w.document.write(`<!DOCTYPE html><html><head><title>${filterSub} - ${lcoCountsStatus} - LCO Counts</title>
                    <style>body{font-family:Arial,sans-serif;padding:20px}table{width:100%;border-collapse:collapse;font-size:12px}th{background:#e0f2fe;padding:8px 10px;text-align:left;font-weight:700;color:#0369a1;border-bottom:2px solid #bae6fd}td{border-bottom:1px solid #e0f2fe}h2{color:#0369a1;margin-bottom:4px}p{color:#64748b;font-size:12px;margin-bottom:16px}.total{background:#0369a1;color:white;padding:10px;border-radius:8px;margin-bottom:16px;display:inline-block}@media print{body{print-color-adjust:exact}}</style>
                    </head><body>
                    <h2>📊 LCO Breakdown — ${filterSub} — ${lcoCountsStatus}</h2>
                    <p>Generated: ${new Date().toLocaleString('en-IN')} | Sub-District: ${filterSub}</p>
                    <div class="total">Total: ${tot.toLocaleString()}</div>
                    <table><thead><tr><th>#</th><th>LCO Code</th><th style="text-align:right">Count</th><th style="text-align:right">Share%</th></tr></thead>
                    <tbody>${rows}</tbody></table>
                    </body></html>`);
                    w.document.close();
                    w.print();
                  }} style={{padding:'4px 10px',background:'#dc2626',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'11px',fontWeight:'600'}}>
                    🖨️ PDF
                  </button>
                  <button onClick={()=>setShowLcoCounts(false)} style={{border:'none',background:'none',cursor:'pointer',color:'#94a3b8',fontSize:'16px'}}>✕</button>
                </div>
              </div>
              {lcoCountsLoading ? (
                <div style={{textAlign:'center',padding:'20px',color:'#94a3b8'}}>Loading...</div>
              ) : (
                <div style={{maxHeight:'320px',overflowY:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:'12px'}}>
                    <thead><tr style={{background:'#e0f2fe',position:'sticky',top:0}}>
                      <th style={{padding:'8px 12px',textAlign:'left',fontWeight:'700',color:'#0369a1'}}>#</th>
                      <th style={{padding:'8px 12px',textAlign:'left',fontWeight:'700',color:'#0369a1'}}>LCO Code</th>
                      <th style={{padding:'8px 12px',textAlign:'right',fontWeight:'700',color:'#0369a1'}}>Count</th>
                      <th style={{padding:'8px 12px',textAlign:'right',fontWeight:'700',color:'#0369a1'}}>Share%</th>
                    </tr></thead>
                    <tbody>
                      {lcoCounts.map((lco,i)=>{
                        const total = lcoCounts.reduce((a,l)=>a+l.count,0);
                        const pct = Math.round(lco.count/total*100*10)/10;
                        return (
                          <tr key={i} style={{borderBottom:'1px solid #e0f2fe',background:i%2?'#f0f9ff':'white'}}>
                            <td style={{padding:'7px 12px',color:'#94a3b8',fontSize:'11px'}}>{i+1}</td>
                            <td style={{padding:'7px 12px',fontWeight:'700',color:'#0369a1'}}>{lco._id||'Unknown'}</td>
                            <td style={{padding:'7px 12px',textAlign:'right',fontWeight:'700'}}>{lco.count.toLocaleString()}</td>
                            <td style={{padding:'7px 12px',textAlign:'right',color:'#64748b'}}>{pct}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Monthly Tracker - Collapsible */}
          <div style={{background:'white',borderRadius:'12px',padding:'16px',marginBottom:'16px',border:'1px solid #f1f5f9'}}>
            <div onClick={loadMonthlyTracker} style={{display:'flex',justifyContent:'space-between',alignItems:'center',cursor:'pointer'}}>
              <div style={{fontWeight:'800',fontSize:'14px',color:'#1e293b'}}>📅 Monthly Tracker <span style={{fontSize:'11px',fontWeight:'400',color:'#64748b'}}>(Month-wise summary with sub-district)</span></div>
              <span style={{fontSize:'12px',color:'#94a3b8',transform:showMonthly?'rotate(180deg)':'rotate(0deg)',display:'inline-block',transition:'transform .2s'}}>▼</span>
            </div>
            {showMonthly && (
              <div style={{marginTop:'12px'}}>
                {monthlyLoading ? <div style={{textAlign:'center',padding:'20px',color:'#94a3b8'}}>Loading...</div> : (
                  <div>
                  <div style={{display:'flex',gap:'6px',marginBottom:'12px',alignItems:'center',flexWrap:'wrap'}}>
                    <span style={{fontSize:'12px',fontWeight:'600',color:'#64748b'}}>Select Year:</span>
                    {[...new Set(monthlyData.map(m=>m.key.split('-')[0]))].sort((a,b)=>b-a).map(y=>(
                      <button key={y} onClick={()=>setMonthlyYear(monthlyYear===y?'':y)}
                        style={{padding:'6px 16px',border:'2px solid',borderRadius:'8px',cursor:'pointer',fontSize:'12px',fontWeight:'700',
                          borderColor:monthlyYear===y?'#2563eb':'#e2e8f0',
                          background:monthlyYear===y?'#2563eb':'white',
                          color:monthlyYear===y?'white':'#64748b'}}>
                        {y} {monthlyYear===y?'▲':'▼'}
                      </button>
                    ))}
                  </div>
                  {monthlyYear && monthlyData.filter(m=>m.key.startsWith(monthlyYear)).map((m,mi)=>(
                    <div key={mi} style={{border:'1px solid #e2e8f0',borderRadius:'10px',marginBottom:'8px',overflow:'hidden'}}>
                      {/* Month header */}
                      <div onClick={()=>setExpandedMonth(expandedMonth===m.key?null:m.key)}
                        style={{padding:'12px 16px',background:expandedMonth===m.key?'#eff6ff':'#f8fafc',cursor:'pointer',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                        <div>
                          <span style={{fontWeight:'800',fontSize:'14px',color:'#1e293b'}}>{m.label}</span>
                          <span style={{fontSize:'12px',color:'#64748b',marginLeft:'12px'}}>{m.days_count} days recorded</span>
                        </div>
                        <div style={{display:'flex',gap:'16px',alignItems:'center'}}>
                          <div style={{textAlign:'right'}}>
                            <div style={{fontSize:'12px',color:'#64748b'}}>End Active</div>
                            <div style={{fontWeight:'800',color:'#2563eb'}}>{m.end_active?.toLocaleString('en-IN')}</div>
                          </div>
                          <div style={{textAlign:'right'}}>
                            <div style={{fontSize:'12px',color:'#64748b'}}>Net Growth</div>
                            <div style={{fontWeight:'800',color:m.net_growth>=0?'#16a34a':'#dc2626'}}>
                              {m.net_growth>=0?'+':''}{m.net_growth?.toLocaleString('en-IN')}
                            </div>
                          </div>
                          <span style={{color:'#94a3b8',fontSize:'12px'}}>{expandedMonth===m.key?'▲':'▼'}</span>
                        </div>
                      </div>
                      {/* Expanded - sub-district breakdown */}
                      {expandedMonth===m.key && (
                        <div style={{padding:'12px 16px'}}>
                          {/* Sub breakdown table */}
                          {m.subs?.length>0 && (
                            <div style={{marginBottom:'12px'}}>
                              <div style={{fontSize:'12px',fontWeight:'700',color:'#64748b',marginBottom:'8px'}}>Sub-District Breakdown</div>
                              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'12px'}}>
                                <thead><tr style={{background:'#f1f5f9'}}>
                                  <th style={{padding:'6px 10px',textAlign:'left',fontWeight:'700',color:'#64748b'}}>Sub-District</th>
                                  <th style={{padding:'6px 10px',textAlign:'right',fontWeight:'700',color:'#64748b'}}>Start Active</th>
                                  <th style={{padding:'6px 10px',textAlign:'right',fontWeight:'700',color:'#64748b'}}>End Active</th>
                                  <th style={{padding:'6px 10px',textAlign:'right',fontWeight:'700',color:'#64748b'}}>Net Growth</th>
                                </tr></thead>
                                <tbody>
                                  {m.subs.map((s,si)=>(
                                    <tr key={si} style={{borderTop:'1px solid #f1f5f9',background:si%2?'#fafafa':'white'}}>
                                      <td style={{padding:'6px 10px',fontWeight:'700',color:'#2563eb'}}>{s.sub_dist}</td>
                                      <td style={{padding:'6px 10px',textAlign:'right'}}>{s.start_active?.toLocaleString('en-IN')}</td>
                                      <td style={{padding:'6px 10px',textAlign:'right',fontWeight:'700'}}>{s.end_active?.toLocaleString('en-IN')}</td>
                                      <td style={{padding:'6px 10px',textAlign:'right',fontWeight:'800',color:s.net_growth>=0?'#16a34a':'#dc2626'}}>
                                        {s.net_growth>=0?'+':''}{s.net_growth?.toLocaleString('en-IN')}
                                      </td>
                                    </tr>
                                  ))}
                                  {/* Total row */}
                                  <tr style={{borderTop:'2px solid #e2e8f0',background:'#eff6ff'}}>
                                    <td style={{padding:'6px 10px',fontWeight:'800',color:'#1e293b'}}>TOTAL</td>
                                    <td style={{padding:'6px 10px',textAlign:'right',fontWeight:'800'}}>{m.start_active?.toLocaleString('en-IN')}</td>
                                    <td style={{padding:'6px 10px',textAlign:'right',fontWeight:'800'}}>{m.end_active?.toLocaleString('en-IN')}</td>
                                    <td style={{padding:'6px 10px',textAlign:'right',fontWeight:'800',color:m.net_growth>=0?'#16a34a':'#dc2626'}}>
                                      {m.net_growth>=0?'+':''}{m.net_growth?.toLocaleString('en-IN')}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          )}
                          {/* Daily breakdown */}
                          <div style={{fontSize:'12px',fontWeight:'700',color:'#64748b',marginBottom:'6px'}}>Daily Breakdown</div>
                          <div style={{maxHeight:'200px',overflowY:'auto'}}>
                            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
                              <thead><tr style={{background:'#f1f5f9',position:'sticky',top:0}}>
                                <th style={{padding:'5px 8px',textAlign:'left',fontWeight:'700',color:'#64748b'}}>Date</th>
                                <th style={{padding:'5px 8px',textAlign:'right',fontWeight:'700',color:'#64748b'}}>Active</th>
                                <th style={{padding:'5px 8px',textAlign:'right',fontWeight:'700',color:'#64748b'}}>Change</th>
                              </tr></thead>
                              <tbody>
                                {m.days.map((d,di)=>{
                                  const prev = di>0?m.days[di-1].active:d.active;
                                  const chg = d.active - prev;
                                  return (
                                    <tr key={di} style={{borderTop:'1px solid #f1f5f9'}}>
                                      <td style={{padding:'4px 8px'}}>{new Date(d.date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</td>
                                      <td style={{padding:'4px 8px',textAlign:'right',fontWeight:'600'}}>{d.active?.toLocaleString('en-IN')}</td>
                                      <td style={{padding:'4px 8px',textAlign:'right',fontWeight:'600',color:chg>0?'#16a34a':chg<0?'#dc2626':'#94a3b8'}}>
                                        {di===0?'-':(chg>=0?'+':'')+chg}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                  }
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Trend Table - Collapsible */}
          {data.trend_dates?.length>0 && (
            <div style={card}>
              <div onClick={()=>setShowTrend(p=>!p)} style={{display:'flex',justifyContent:'space-between',alignItems:'center',cursor:'pointer',marginBottom:showTrend?'12px':'0'}}>
                <div style={{fontWeight:'800',fontSize:'14px',color:'#1e293b'}}>📊 Daily Tracker <span style={{fontSize:'11px',fontWeight:'400',color:'#64748b'}}>({data.trend_dates?.length} days)</span></div>
                <span style={{fontSize:'12px',color:'#94a3b8',transform:showTrend?'rotate(180deg)':'rotate(0deg)',display:'inline-block',transition:'transform .2s'}}>▼</span>
              </div>
              {showTrend && <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'12px'}}>
                  <thead>
                    <tr style={{background:'#f8fafc'}}>
                      <th style={{padding:'8px 10px',textAlign:'left',fontWeight:'700'}}>Date</th>
                      <th style={{padding:'8px 10px',textAlign:'center',color:'#15803d'}}>Active</th>
                      <th style={{padding:'8px 10px',textAlign:'center',color:'#dc2626'}}>Suspended</th>
                      <th style={{padding:'8px 10px',textAlign:'center',color:'#d97706'}}>Inactive</th>
                      <th style={{padding:'8px 10px',textAlign:'center',color:'#7c3aed'}}>Pending</th>
                      <th style={{padding:'8px 10px',textAlign:'center',color:'#64748b'}}>Dormant</th>
                      <th style={{padding:'8px 10px',textAlign:'center'}}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.trend_dates.filter(d=>{
                      const days = period==='daily'?1:period==='weekly'?7:30;
                      const cutoff = new Date(); cutoff.setDate(cutoff.getDate()-days);
                      return new Date(d) >= cutoff;
                    }).reverse().map((d,i)=>{
                      const t = data.trend[d]||{};
                      const total = Object.values(t).reduce((a,b)=>a+b,0);
                      const prevD = data.trend_dates[data.trend_dates.indexOf(d)-1];
                      const prevActive = prevD?(data.trend[prevD]?.['Active']||0):0;
                      const currActive = t['Active']||0;
                      const diff = prevD?currActive-prevActive:0;
                      return (
                        <tr key={d} style={{borderTop:'1px solid #f1f5f9',background:i===0?'#f0fdf4':'white'}}>
                          <td style={{padding:'8px 10px',fontWeight:i===0?'700':'400'}}>
                            {new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}
                            {i===0&&<span style={{marginLeft:'6px',fontSize:'10px',background:'#dcfce7',color:'#15803d',padding:'1px 6px',borderRadius:'10px'}}>Today</span>}
                          </td>
                          <td style={{padding:'8px 10px',textAlign:'center',color:'#15803d',fontWeight:'700'}}>
                            {(t['Active']||0).toLocaleString()}
                            {diff!==0&&<span style={{marginLeft:'4px',fontSize:'10px',color:diff>0?'#15803d':'#dc2626'}}>{diff>0?'↑':'↓'}{Math.abs(diff)}</span>}
                          </td>
                          <td style={{padding:'8px 10px',textAlign:'center',color:'#dc2626'}}>{(t['Suspended']||0).toLocaleString()}</td>
                          <td style={{padding:'8px 10px',textAlign:'center',color:'#d97706'}}>{(t['Inactive']||0).toLocaleString()}</td>
                          <td style={{padding:'8px 10px',textAlign:'center',color:'#7c3aed'}}>{(t['New - Activation Pending']||0).toLocaleString()}</td>
                          <td style={{padding:'8px 10px',textAlign:'center',color:'#64748b'}}>{(t['Dormant']||0).toLocaleString()}</td>
                          <td style={{padding:'8px 10px',textAlign:'center',fontWeight:'600'}}>{total.toLocaleString()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>}
            </div>
          )}

          {/* New Active breakdown panel */}
          {showStatusChanges && (
            <div style={{...card, marginBottom:'16px', border:'2px solid #16a34a'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'12px'}}>
                <div style={{fontWeight:'700',fontSize:'13px',color:'#15803d'}}>🆕 New Active Breakdown {statusChanges?.date ? `— ${statusChanges.date}` : ''}</div>
                <button onClick={()=>setShowStatusChanges(false)} style={{border:'none',background:'none',cursor:'pointer',fontSize:'16px'}}>✕</button>
              </div>
              {statusChangesLoading ? <div style={{textAlign:'center',padding:'20px',color:'#64748b'}}>Loading...</div> : (
                !statusChanges?.total ? (
                  <div style={{textAlign:'center',padding:'20px',color:'#64748b',fontSize:'13px'}}>No status changes found. Upload new BMS data to track individual changes.</div>
                ) : (
                  <div>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:'8px',marginBottom:'12px'}}>
                      {(statusChanges?.breakdown||[]).map(b=>(
                        <div key={b._id} style={{background:'#f0fdf4',borderRadius:'8px',padding:'10px',textAlign:'center'}}>
                          <div style={{fontSize:'20px',fontWeight:'800',color:'#15803d'}}>{b.count}</div>
                          <div style={{fontSize:'10px',color:'#64748b',marginTop:'2px'}}>{b._id} → Active</div>
                        </div>
                      ))}
                      <div style={{background:'#eff6ff',borderRadius:'8px',padding:'10px',textAlign:'center'}}>
                        <div style={{fontSize:'20px',fontWeight:'800',color:'#2563eb'}}>{statusChanges?.total||0}</div>
                        <div style={{fontSize:'10px',color:'#64748b',marginTop:'2px'}}>Total New Active</div>
                      </div>
                    </div>
                    {(statusChanges?.users||[]).length>0 && (
                      <div style={{maxHeight:'200px',overflowY:'auto',border:'1px solid #e2e8f0',borderRadius:'8px'}}>
                        <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
                          <thead><tr style={{background:'#f8fafc'}}>
                            <th style={{padding:'6px 10px',textAlign:'left',fontWeight:600,color:'#64748b'}}>Username</th>
                            <th style={{padding:'6px 10px',textAlign:'left',fontWeight:600,color:'#64748b'}}>Previous Status</th>
                            <th style={{padding:'6px 10px',textAlign:'left',fontWeight:600,color:'#64748b'}}>Sub District</th>
                          </tr></thead>
                          <tbody>
                            {(statusChanges?.users||[]).map((u,i)=>(
                              <tr key={i} style={{borderTop:'1px solid #f1f5f9'}}>
                                <td style={{padding:'5px 10px',fontFamily:'monospace'}}>{u.username}</td>
                                <td style={{padding:'5px 10px'}}><span style={{background:'#fef2f2',color:'#dc2626',padding:'1px 6px',borderRadius:'4px',fontSize:'10px'}}>{u.prev_status}</span></td>
                                <td style={{padding:'5px 10px',color:'#64748b'}}>{u.sub_dist_code||'KB13'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )
              )}
            </div>
          )}

          {/* Pending Activation Breakdown */}
          {showPending && (
            <div style={{background:'#f5f3ff',borderRadius:'12px',padding:'16px',marginBottom:'16px',border:'1px solid #e9d5ff'}}>
              {isSubDistAdmin ? (
                <div>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'12px'}}>
                    <div style={{fontWeight:'700',fontSize:'13px',color:'#7c3aed'}}>⏳ Pending Activation — LCO Breakdown ({mySubCode})</div>
                    <div style={{fontSize:'12px',color:'#7c3aed',fontWeight:'700'}}>Total: {data.summary.pending}</div>
                  </div>
                  {pendingLcoLoading ? (
                    <div style={{textAlign:'center',padding:'20px',color:'#94a3b8'}}>Loading LCO data...</div>
                  ) : pendingByLco.length===0 ? (
                    <div style={{textAlign:'center',padding:'20px',color:'#94a3b8'}}>No pending data found</div>
                  ) : (
                    <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
                      {pendingByLco.map((lco,i)=>(
                        <div key={i} style={{background:'white',borderRadius:'8px',border:'1px solid #e9d5ff',overflow:'hidden'}}>
                          <div onClick={()=>setExpandedLcos(p=>({...p,[lco._id]:!p[lco._id]}))}
                            style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 14px',cursor:'pointer',userSelect:'none'}}>
                            <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                              <span style={{fontSize:'11px',color:'#c4b5fd',display:'inline-block',transform:expandedLcos[lco._id]?'rotate(90deg)':'none',transition:'transform 0.2s'}}>▶</span>
                              <span style={{fontWeight:'700',color:'#7c3aed'}}>{lco._id||'Unknown'}</span>
                            </div>
                            <span style={{background:'#7c3aed',color:'white',borderRadius:'20px',padding:'2px 10px',fontSize:'12px',fontWeight:'700'}}>{lco.count} pending</span>
                          </div>
                          {expandedLcos[lco._id] && (
                            <div style={{borderTop:'1px solid #e9d5ff',padding:'8px 14px'}}>
                              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
                                <thead><tr style={{background:'#faf5ff'}}>
                                  {['Username','Name','Mobile','Address'].map(h=>(
                                    <th key={h} style={{padding:'6px 8px',textAlign:'left',fontWeight:'600',color:'#7c3aed',borderBottom:'1px solid #e9d5ff'}}>{h}</th>
                                  ))}
                                </tr></thead>
                                <tbody>
                                  {lco.customers.map((cu,j)=>(
                                    <tr key={j} style={{borderTop:'1px solid #f5f3ff',background:j%2?'#faf5ff':'white'}}>
                                      <td style={{padding:'6px 8px',fontFamily:'monospace',color:'#7c3aed',fontWeight:'600'}}>{cu.username}</td>
                                      <td style={{padding:'6px 8px'}}>{cu.name}</td>
                                      <td style={{padding:'6px 8px',color:'#0891b2',fontWeight:'600'}}>{cu.mobile}</td>
                                      <td style={{padding:'6px 8px',color:'#64748b',fontSize:'10px'}}>{(cu.address||'').substring(0,40)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div style={{fontWeight:'700',fontSize:'13px',color:'#7c3aed',marginBottom:'12px'}}>⏳ Pending Activation — Sub-District Breakdown</div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:'10px'}}>
                    {pendingSubs.map((s,i)=>(
                      <div key={i} style={{background:'white',borderRadius:'8px',padding:'12px',border:'1px solid #e9d5ff'}}>
                        <div style={{fontWeight:'700',fontSize:'13px',color:'#7c3aed'}}>{s._id||'KB13'}</div>
                        <div style={{fontSize:'28px',fontWeight:'800',color:'#7c3aed',margin:'4px 0'}}>{s.pending}</div>
                        <div style={{fontSize:'11px',color:'#94a3b8'}}>of {s.total} total ({Math.round(s.pending/Math.max(s.total,1)*100)}%)</div>
                      </div>
                    ))}
                  </div>
                  <div style={{marginTop:'12px',padding:'10px',background:'white',borderRadius:'8px',display:'flex',justifyContent:'space-between',fontSize:'13px',fontWeight:'700'}}>
                    <span style={{color:'#7c3aed'}}>Total Pending</span>
                    <span style={{color:'#7c3aed'}}>{data.summary.pending?.toLocaleString()}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Sub-District Breakdown */}
          <div style={card}>
            <div style={{fontWeight:'800',fontSize:'14px',marginBottom:'12px',color:'#1e293b'}}>🏘️ Sub-District Breakdown</div>
            <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
              {data.sub_districts.map((s,i)=>{
                const rate = Math.round(s.active/Math.max(s.total,1)*100);
                const isExp = expandedSubs[s._id];
                const health = rate>=85?'✅':rate>=70?'🟡':'🔴';
                const bc = rate>=85?'#e2e8f0':rate>=70?'#fcd34d':'#fca5a5';
                const hbg = rate>=85?'#f8fafc':rate>=70?'#fffbeb':'#fef2f2';
                return (
                  <div key={i} style={{borderRadius:'8px',border:`1px solid ${bc}`,overflow:'hidden'}}>
                    <div onClick={()=>setExpandedSubs(p=>({...p,[s._id]:!p[s._id]}))}
                      style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 14px',cursor:'pointer',background:hbg,userSelect:'none'}}>
                      <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                        <span style={{fontSize:'16px'}}>{health}</span>
                        <div>
                          <span style={{fontWeight:'700',fontSize:'13px',color:'#1e293b'}}>{s._id||'KB13'}</span>
                          <div style={{display:'flex',gap:'10px',marginTop:'2px',flexWrap:'wrap'}}>
                            <span style={{fontSize:'10px',color:'#15803d',fontWeight:'700'}}>✅ {s.active?.toLocaleString()} active ({rate}%)</span>
                            {s.suspended>0&&<span style={{fontSize:'10px',color:'#dc2626',fontWeight:'700'}}>🚫 {s.suspended?.toLocaleString()} suspended</span>}
                            {s.pending>0&&<span style={{fontSize:'10px',color:'#7c3aed',fontWeight:'700'}}>⏳ {s.pending} pending</span>}
                            <span style={{fontSize:'10px',color:'#64748b'}}>Total: {s.total?.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                      <span style={{fontSize:'12px',color:'#94a3b8',transform:isExp?'rotate(180deg)':'rotate(0deg)',display:'inline-block',transition:'transform .2s'}}>▼</span>
                    </div>
                    {isExp && (
                      <div style={{padding:'12px 14px',borderTop:`1px solid ${bc}`,background:'#fafafa'}}>
                        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(100px,1fr))',gap:'8px'}}>
                          {[
                            {l:'Total',v:s.total,c:'#2563eb'},{l:'Active',v:s.active,c:'#15803d'},
                            {l:'Suspended',v:s.suspended,c:'#dc2626'},{l:'Inactive',v:s.inactive,c:'#d97706'},
                            {l:'Pending',v:s.pending,c:'#7c3aed'},{l:'Dormant',v:s.dormant,c:'#64748b'}
                          ].map(x=>(
                            <div key={x.l} style={{background:'white',borderRadius:'6px',padding:'8px',textAlign:'center',border:'1px solid #e2e8f0'}}>
                              <div style={{fontSize:'16px',fontWeight:'800',color:x.c}}>{x.v?.toLocaleString()}</div>
                              <div style={{fontSize:'9px',color:'#64748b'}}>{x.l}</div>
                            </div>
                          ))}
                        </div>
                        {(!s._id || s._id === 'KB13') && s.total <= 20 && (
                        <div style={{marginTop:'10px',display:'flex',gap:'8px',alignItems:'center'}}>
                          <button onClick={()=>fetchBmsUsers(s._id||'KB13')} style={{padding:'5px 12px',fontSize:'11px',background:'#2563eb',color:'#fff',border:'none',borderRadius:'6px',cursor:'pointer',fontWeight:600}}>
                            {bmsUsersSub===(s._id||'KB13')?'🔼 Hide Users':'🔍 View Users'}
                          </button>
                          {bmsUsersLoading && bmsUsersSub===(s._id||'KB13') && <span style={{fontSize:'11px',color:'#64748b'}}>Loading...</span>}
                        </div>
                        )}
                        {bmsUsersSub===(s._id||'KB13') && bmsUsers && (
                          <div style={{marginTop:'10px',overflowX:'auto'}}>
                            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
                              <thead><tr style={{background:'#f1f5f9'}}>
                                <th style={{padding:'6px 8px',textAlign:'left'}}>Username</th>
                                <th style={{padding:'6px 8px',textAlign:'left'}}>Name</th>
                                <th style={{padding:'6px 8px',textAlign:'left'}}>Mobile</th>
                                <th style={{padding:'6px 8px',textAlign:'left'}}>Plan</th>
                                <th style={{padding:'6px 8px',textAlign:'left'}}>Status</th>
                                <th style={{padding:'6px 8px',textAlign:'left'}}>Expiry</th>
                              </tr></thead>
                              <tbody>
                                {bmsUsers.map((u,j)=>(
                                  <tr key={j} style={{borderTop:'1px solid #f1f5f9',background:j%2?'#f8fafc':'white'}}>
                                    <td style={{padding:'6px 8px',fontFamily:'monospace',color:'#2563eb'}}>{u.username}</td>
                                    <td style={{padding:'6px 8px'}}>{u.name||'-'}</td>
                                    <td style={{padding:'6px 8px'}}>{u.mobile||'-'}</td>
                                    <td style={{padding:'6px 8px',color:'#7c3aed'}}>{u.plan||'-'}</td>
                                    <td style={{padding:'6px 8px'}}><span style={{background:u.status==='Active'?'#dcfce7':'#fee2e2',color:u.status==='Active'?'#15803d':'#dc2626',padding:'2px 6px',borderRadius:'4px',fontWeight:600}}>{u.status}</span></td>
                                    <td style={{padding:'6px 8px',color:'#64748b'}}>{u.expiry||'-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top Plans */}
          {data.top_plans?.length>0 && (
            <div style={card}>
              <div style={{fontWeight:'800',fontSize:'14px',marginBottom:'12px',color:'#1e293b'}}>📋 Top Active Plans</div>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'12px'}}>
                  <thead><tr style={{background:'#f8fafc'}}>
                    <th style={{padding:'8px 10px',textAlign:'left'}}>Plan</th>
                    <th style={{padding:'8px 10px',textAlign:'center'}}>Subscribers</th>
                    <th style={{padding:'8px 10px',textAlign:'left'}}>Distribution</th>
                  </tr></thead>
                  <tbody>
                    {data.top_plans.map((p,i)=>{
                      const pct = Math.round(p.count/data.summary.active*100);
                      return (
                        <tr key={i} style={{borderTop:'1px solid #f1f5f9'}}>
                          <td style={{padding:'8px 10px',fontWeight:'600'}}>{p._id}</td>
                          <td style={{padding:'8px 10px',textAlign:'center',fontWeight:'700',color:'#2563eb'}}>{p.count?.toLocaleString()}</td>
                          <td style={{padding:'8px 10px'}}>
                            <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                              <div style={{flex:1,background:'#f1f5f9',borderRadius:'20px',height:'6px',overflow:'hidden'}}>
                                <div style={{width:pct+'%',height:'100%',background:'#2563eb',borderRadius:'20px'}}/>
                              </div>
                              <span style={{fontSize:'11px',color:'#64748b',minWidth:'30px'}}>{pct}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
